/** Python's `round(x, n)`, bit for bit.
 *
 * The logger's analysis rounds nearly everything it answers — distances to a
 * decimetre, speeds to a tenth, resampled channels to four places — and the
 * portal's port is tested against what the logger answered. `Math.round(x *
 * 10 ** n) / 10 ** n` is not the same function: it rounds halves away from
 * negative infinity where Python rounds them to even, and it rounds the
 * floating-point PRODUCT `x * 10 ** n` where Python rounds the exact value of
 * `x`. Both differences bite on real data. Sample distances are stored to a
 * centimetre, so a corner window placed 75 m from a sample at 231.25 m ends
 * at 156.25 m exactly — a true tie, which Python reports as 156.2 and the
 * naive version as 156.3. And `0.15 * 10` is exactly 1.5 in floating point
 * although 0.15 itself is a little under, so the naive version says 0.2 where
 * Python, reading the exact value, says 0.1.
 *
 * So this does what CPython does: decide from the exact value. The product
 * `p = x * 10 ** n` carries a rounding error that Dekker's two-product
 * recovers exactly; `p` lands on a half only when the true product is within
 * half an ulp of one, and the recovered error then says which side it is on
 * (or that it is a true tie, which goes to even). The integer that results is
 * divided by the same power of ten, and a quotient of two exact doubles is
 * correctly rounded — the same double Python gets from its decimal string.
 *
 * The sign of zero follows `x`, as Python's does: `round(-0.04, 1)` is
 * `-0.0`. Callers that want a plain zero add `+ 0`, which is what the
 * Python's `round(v, 1) + 0.0` idiom does.
 */

/** Veltkamp's splitting constant, 2^27 + 1. */
const SPLITTER = 134217729;

const POW10 = [1, 10, 100, 1e3, 1e4, 1e5, 1e6, 1e7, 1e8, 1e9];

/** The exact error of the floating-point product `p = a * b` (Dekker). */
function productError(a: number, b: number, p: number): number {
  let t = SPLITTER * a;
  const ah = t - (t - a);
  const al = a - ah;
  t = SPLITTER * b;
  const bh = t - (t - b);
  const bl = b - bh;
  return ah * bh - p + ah * bl + al * bh + al * bl;
}

/** `round(x, digits)` as Python computes it. `digits` must be 0..9. */
export function roundTo(x: number, digits = 0): number {
  if (!Number.isFinite(x)) return x;
  const f = POW10[digits];
  if (f === undefined) throw new RangeError(`roundTo: ${digits} digits is out of range`);
  const p = x * f;
  // For |p| below 2^52 the fractional part comes out exact: it is a multiple
  // of p's ulp with fewer than 53 significant bits.
  const whole = Math.floor(p);
  const frac = p - whole;
  let r: number;
  if (frac === 0.5) {
    const err = digits === 0 ? 0 : productError(x, f, p);
    if (err === 0) r = whole % 2 === 0 ? whole : whole + 1;
    else r = err > 0 ? whole + 1 : whole;
  } else {
    r = frac < 0.5 ? whole : whole + 1;
  }
  if (r === 0) return x < 0 || Object.is(x, -0) ? -0 : 0;
  return digits === 0 ? r : r / f;
}

/** Python's `repr(float)` for the handful of places a number is spoken: an
 *  integral value keeps its `.0`. */
export function pyFloat(v: number): string {
  if (Number.isInteger(v) && Math.abs(v) < 1e16) return `${v}.0`;
  return String(v);
}
